import styled from "styled-components";
import { RATypography, RAButtonPrimary, RARadioButton, RACheckbox } from "@/components/customs";
import { Row, Col, Divider } from "antd";
import { initFormAddAlertEmptyLegs } from "@/utils/initializing";
import { SvgDirection, SvgLocation } from "@/assets/icons";
import { RAInputSearchAirport } from "@/components/atoms";
import { useEffect, useRef, useState } from "react";
import { isEmpty } from "lodash";
import { InputAlertDepartureWindow } from "./InputAlertDepartureWindow";

export function FormAddAlert({ data = initFormAddAlertEmptyLegs, onChange, onSubmitEmptyLegsAlert }) {
	const { type, type_airport = [] } = data;
	const [error, setError] = useState({});

	const routeDepartureRef = useRef(null);
	const routeArrivalRef = useRef(null);
	const airportRef = useRef(null);

	// Test-only hook: same window.__airportSearchRefs mechanism already used by
	// FormRequestFlightItem/FormAirFreightInfo, lets Playwright trigger the
	// debounced airport search directly (automated keystrokes don't reach
	// rc-select's internal onSearch path).
	useEffect(() => {
		if (process.env.NEXT_PUBLIC_ENV === "prod") return;
		if (!window.__airportSearchRefs) window.__airportSearchRefs = {};
		window.__airportSearchRefs["emptyLegAlert_routeDeparture"] = routeDepartureRef.current;
		window.__airportSearchRefs["emptyLegAlert_routeArrival"] = routeArrivalRef.current;
		window.__airportSearchRefs["emptyLegAlert_airport"] = airportRef.current;
	});

	const validateFormAddAlert = () => {
		let valid = true;
		let errs = {};
		if (data.type === "ROUTE" && !data.from_airport) {
			errs.from_airport = "Please select departure airport.";
		}
		if (data.type === "ROUTE" && !data.to_airport) {
			errs.to_airport = "Please select arrival airport.";
		}
		if (data.type === "AIRPORT" && !data.airport) {
			errs.airport = "Please select airport.";
		}
		if (data.type === "AIRPORT" && (!data.type_airport || data.type_airport.length === 0)) {
			errs.type_airport = "Please select Departure or Arrival.";
		}
		if (data.from_datetime && !data.to_datetime) {
			errs.departure_window = "Please select time frame.";
		}

		if (!isEmpty(errs)) {
			valid = false;
			setError(errs);
		}

		return valid;
	};

	const handleAddAlert = () => {
		let validForm = validateFormAddAlert();
		if (!validForm) return;

		onSubmitEmptyLegsAlert();
	};

	const _renderHeader = () => {
		return (
			<Row>
				<Col md={24}>
					<WrapperContent>
						<RATypography variant="h3">Add Alert</RATypography>
					</WrapperContent>
				</Col>
			</Row>
		);
	};

	const _renderFlightOptions = () => {
		return (
			<Row gutter={[40, 12]} align="middle">
				<Col>
					<ButtonRequestOption
						data-testid="EmptyLegAlert_routeRadio"
						title="Route"
						active={type === "ROUTE"}
						onChange={() => onChange({ ...data, type: "ROUTE" })}
					/>
				</Col>
				<Col>
					<ButtonRequestOption
						data-testid="EmptyLegAlert_airportRadio"
						title="Airport"
						active={type === "AIRPORT"}
						onChange={() => onChange({ ...data, type: "AIRPORT" })}
					/>
				</Col>
			</Row>
		);
	};

	const _renderDepartureAirport = () => {
		if (data.type === "ROUTE") {
			return (
				<Row gutter={[20, 20]} align="middle" style={{ marginTop: "20px" }}>
					<Col span={24}>
						<RAInputSearchAirport
							key="route-departure"
							ref={routeDepartureRef}
							data-testid="EmptyLegAlert_departureAirport"
							title="Departure"
							prefix={<SvgDirection color="#595959" />}
							placeholder="Select departure airport"
							value={data?.from_airport}
							error={error?.from_airport || null}
							onSelect={(value) => {
								onChange({
									...data,
									from_airport: value,
								});
							}}
						/>
					</Col>
					<Col span={24}>
						<RAInputSearchAirport
							key="route-arrival"
							ref={routeArrivalRef}
							data-testid="EmptyLegAlert_arrivalAirport"
							title="Arrival"
							prefix={<SvgLocation color="#595959" />}
							placeholder="Select arrival airport"
							value={data?.to_airport}
							error={error?.to_airport || null}
							onSelect={(value) => {
								onChange({
									...data,
									to_airport: value,
								});
							}}
						/>
					</Col>
				</Row>
			);
		} else if (type === "AIRPORT") {
			return (
				<Row gutter={[20, 20]} align="middle" style={{ marginTop: "20px" }}>
					<Col span={24}>
						<RAInputSearchAirport
							key="airport-departure-arrival"
							ref={airportRef}
							data-testid="EmptyLegAlert_airport"
							title="Airport"
							prefix={<SvgDirection color="#595959" />}
							placeholder="Select airport"
							value={data?.airport}
							error={error?.airport || null}
							onSelect={(value) => {
								onChange({
									...data,
									airport: value,
								});
							}}
						/>
					</Col>
					<Col span={24}>
						<RATypography color="#595959" variant="sub-heading4" bottom={8}>
							Departure or Arrival
						</RATypography>
						<Row gutter={[40, 12]}>
							<Col>
								<RACheckbox
									data-testid="EmptyLegAlert_departureCheckbox"
									checked={type_airport.includes("DEPARTURE")}
									title="Departure"
									onChange={() => {
										let newTypeAirport = [...type_airport];
										let indexTypeAirport = newTypeAirport.findIndex((v) => v === "DEPARTURE");
										//
										if (indexTypeAirport > -1) {
											newTypeAirport.splice(indexTypeAirport, 1);
										} else {
											newTypeAirport.push("DEPARTURE");
										}
										onChange({
											...data,
											type_airport: newTypeAirport,
										});
									}}
								/>
							</Col>
							<Col>
								<RACheckbox
									data-testid="EmptyLegAlert_arrivalCheckbox"
									checked={type_airport.includes("ARRIVAL")}
									title="Arrival"
									onChange={() => {
										let newTypeAirport = [...type_airport];
										let indexTypeAirport = newTypeAirport.findIndex((v) => v === "ARRIVAL");
										if (indexTypeAirport > -1) {
											newTypeAirport.splice(indexTypeAirport, 1);
										} else {
											newTypeAirport.push("ARRIVAL");
										}
										onChange({
											...data,
											type_airport: newTypeAirport,
										});
									}}
								/>
							</Col>
						</Row>
						{!!error?.type_airport && (
							<RATypography variant="body3" color="#BF1A2F" top="4px">
								{error?.type_airport}
							</RATypography>
						)}
					</Col>
				</Row>
			);
		}
	};

	const _renderAlertInfo = () => {
		return (
			<div>
				{_renderDepartureAirport()}
				<Row gutter={[20, 20]} align="middle" style={{ marginTop: "20px" }}>
					<Col span={24}>
						<InputAlertDepartureWindow
							from_datetime={data.from_datetime}
							to_datetime={data.to_datetime}
							error={error}
							onChange={(values) => {
								onChange({
									...data,
									...values,
								});
							}}
						/>
					</Col>
				</Row>
			</div>
		);
	};

	return (
		<ContainerRequestFlight data-testid="AddAlertModal_content">
			{_renderHeader()}
			<Divider />
			{_renderFlightOptions()}
			{_renderAlertInfo()}
			<Row justify="center" align="middle" style={{ marginTop: "40px" }}>
				<RAButtonPrimary
					data-testid="EmptyLegAlert_submitBtn"
					title="SUBMIT"
					onClick={() => handleAddAlert()}
					width="160px"
				/>
			</Row>
		</ContainerRequestFlight>
	);
}

const ButtonRequestOption = ({ active, onChange, title = "", "data-testid": dataTestId = null }) => (
	<ButtonOption
		align="middle"
		justify="center"
		onClick={onChange}
		active={active}
		gutter={{ xs: 8, md: 20 }}
		data-testid={dataTestId}
	>
		<Col>
			<RARadioButton checked={active} title={title} onChange={() => {}} />
		</Col>
	</ButtonOption>
);

const ContainerRequestFlight = styled.div`
	width: "100%";
	padding: 30px 30px 30px 30px;
	@media (max-width: 576px) {
		padding: 0;
	}
`;
const WrapperContent = styled.div`
	padding: 0px 20px;
	flex-direction: column;
	align-items: center;
	border-left: 2px solid #ffb11a;
`;
const ButtonOption = styled(Row)`
	padding: 12px 20px;
	flex-wrap: nowrap;
	border: 1px solid #e9e8e8;
	border-radius: 8px;
	cursor: pointer;
	${({ active }) =>
		active &&
		`
        border: 1px solid #ffc34d;
    `}
`;
